import type { Entity } from '@/types/generated'

/**
 * Extract the field name from a tag.
 * Example: 'entities.field.first_name' → 'first_name'
 *
 * @param tag - The tag string (e.g., 'entities.field.first_name')
 * @returns The field name or null if tag doesn't match pattern
 */
export const extractFieldNameFromTag = (tag: string | null | undefined): string | null => {
  if (!tag) return null
  const match = tag.match(/^entities\.field\.(.+)$/)
  return match ? match[1] : null
}

/**
 * Build prefill data dynamically based on form fields.
 * Only extracts fields that are actually used in the form.
 * This is NOT hardcoded - it reads the tags from form_fields and extracts only what's needed.
 *
 * @param formFields - Array of form field objects with tag and tag_relationship
 * @param caseData - Case data containing client and opponent entities
 * @returns Object mapping relationship types to their prefill data
 *
 * @example
 * const formFields = [
 *   { tag: 'entities.field.first_name', tag_relationship: 'entities.client' },
 *   { tag: 'entities.field.last_name', tag_relationship: 'entities.client' }
 * ]
 * const caseData = {
 *   client: { first_name: 'John', last_name: 'Doe', email: 'john@example.com' },
 *   opponent: { first_name: 'Jane' }
 * }
 * const prefillData = buildPrefillDataFromFormFields(formFields, caseData)
 * // Returns: {
 * //   'entities.client': {
 * //     'entities.field.first_name': 'John',
 * //     'entities.field.last_name': 'Doe'
 * //   }
 * // }
 * // Note: email is NOT included because no form field uses it
 */
export const buildPrefillDataFromFormFields = (
  formFields: any[],
  caseData: { client?: Entity; opponent?: Entity } | undefined
): Record<string, Record<string, any>> => {
  if (!caseData) return {}

  const prefillData: Record<string, Record<string, any>> = {}

  // Map relationship to entity
  const entityMap: Record<string, Entity | undefined> = {
    'entities.client': caseData.client,
    'entities.opponent': caseData.opponent
  }

  // Iterate through form fields and extract only the needed data
  formFields.forEach((field) => {
    const tag = field.tag
    const tagRelationship = field.tag_relationship

    if (!tag || !tagRelationship) return

    // Extract field name from tag (e.g., 'entities.field.first_name' → 'first_name')
    const fieldName = extractFieldNameFromTag(tag)
    if (!fieldName) return

    // Get the entity for this relationship
    const entity = entityMap[tagRelationship]
    if (!entity) return

    // Extract the value from the entity using the field name
    const value = (entity as any)[fieldName]
    if (value != null) {
      // Initialize relationship object if needed
      if (!prefillData[tagRelationship]) {
        prefillData[tagRelationship] = {}
      }
      // Store the value using the full tag as key
      prefillData[tagRelationship][tag] = value
    }
  })

  return prefillData
}

/**
 * Prefill form fields based on their tag and tag_relationship.
 *
 * @param formFields - Array of form field objects
 * @param prefillDataByRelationship - Prefill data organized by relationship
 * @param onlyEmpty - If true, only prefill fields that don't already have a value
 * @returns Record<uuid, value> map of prefilled values
 *
 * @example
 * const fields = [
 *   { uuid: '1', tag: 'entities.field.first_name', tag_relationship: 'entities.client', value: '' }
 * ]
 * const prefillData = {
 *   'entities.client': { 'entities.field.first_name': 'John' }
 * }
 * const prefilled = prefillFormFields(fields, prefillData)
 * // Returns: { '1': 'John' }
 */
export const prefillFormFields = (
  formFields: any[],
  prefillDataByRelationship: Record<string, Record<string, any>>,
  onlyEmpty: boolean = true
): Record<string, any> => {
  const fieldValues: Record<string, any> = {}

  formFields.forEach((field) => {
    const tag = field.tag
    const tagRelationship = field.tag_relationship

    // Skip fields without proper tag configuration
    if (!tag || !tagRelationship) return

    // Skip fields that already have a value if onlyEmpty is true
    if (onlyEmpty && field.value) return

    // Check if we have prefill data for this relationship
    const relationshipData = prefillDataByRelationship[tagRelationship]
    if (relationshipData && relationshipData[tag]) {
      fieldValues[field.uuid] = relationshipData[tag]
    }
  })

  return fieldValues
}
